import type { CSSProperties } from 'react'

/** مقاساتُ المنشور بالبكسل الفعليّ — ما تطلبه إنستغرام وفيسبوك. */
export const SIZES = {
  SQUARE: { w: 1080, h: 1080, label: 'منشور مربّع' },
  PORTRAIT: { w: 1080, h: 1350, label: 'منشور طوليّ' },
  STORY: { w: 1080, h: 1920, label: 'ستوري' },
} as const

export type SizeKey = keyof typeof SIZES

export const TEMPLATES = {
  classic: 'بسيط',
  offer: 'عرض',
  new: 'وصل حديثًا',
} as const

export type TemplateKey = keyof typeof TEMPLATES

/** ما يملأ التصميم — من الصنف تلقائيًّا، ويعدّله المالك. */
export type Design = {
  template: TemplateKey
  size: SizeKey
  headline: string
  subline: string
  price: number | null
  regularPrice: number | null
  showPrice: boolean
  badge: string
  accent: string
  background: string
  footer: string
  /** صورةُ الصنف data: URL — لا رابطٌ خلف تسجيل دخول، فيُرسم بلا قيود المصدر. */
  image: string | null
  /** شعارُ المحلّ data: URL. */
  logo: string | null
}

/** ألوانُ الهويّة — من الشعار نفسِه (انظر styles.css في الموقع). */
export const SWATCHES = ['#fde00a', '#f79705', '#f0a202', '#191a1e', '#e53935', '#1e88e5', '#ffffff']

const money = (v: number) => `${v.toFixed(3).replace(/\.?0+$/, '')} دينار`

/** نصٌّ فاتحٌ على الغامق وغامقٌ على الفاتح — يُحسب ولا يُختار. */
function inkOn(hex: string): string {
  const n = parseInt(hex.replace('#', '').padEnd(6, '0').slice(0, 6), 16)
  const lum = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255

  return lum > 0.6 ? '#17181c' : '#ffffff'
}

const FONT = '"Segoe UI", "Noto Sans Arabic", Tahoma, system-ui, sans-serif'

/**
 * التصميمُ نفسُه — <b>بالبكسل الفعليّ</b>، ويُصغَّر للمعاينة بـCSS لا بإعادة رسم.
 * <p>
 * ⚠ <b>فالمعاينةُ هي ما يُنشر حرفًا بحرف</b>: العقدةُ نفسُها تُحوَّل JPEG
 * (<c>html-to-image</c>) في المتصفّح، والعربيّةُ يرسمها محرّكُ المتصفّح.
 * </p>
 */
export function PostDesign({ d }: { d: Design }) {
  const { w, h } = SIZES[d.size]
  const ink = inkOn(d.background)
  const onAccent = inkOn(d.accent)
  const story = d.size === 'STORY'
  const offer = d.template === 'offer' && d.regularPrice !== null && d.price !== null && d.regularPrice > d.price

  const root: CSSProperties = {
    width: w,
    height: h,
    position: 'relative',
    overflow: 'hidden',
    direction: 'rtl',
    fontFamily: FONT,
    color: ink,
    background:
      d.template === 'new'
        ? `radial-gradient(900px 700px at 85% 10%, ${d.accent}55, transparent 60%), ${d.background}`
        : d.template === 'offer'
          ? `linear-gradient(160deg, ${d.background} 55%, ${d.accent}33)`
          : d.background,
    display: 'flex',
    flexDirection: 'column',
    padding: story ? '170px 80px 200px' : '56px 64px 52px',
    boxSizing: 'border-box',
  }

  return (
    <div style={root}>
      {/* الرأس: الشعارُ والشارة */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 96 }}>
        {d.logo ? <img src={d.logo} alt="" style={{ height: 88, borderRadius: 20 }} /> : <span />}
        {d.badge && (
          <span
            style={{
              background: d.accent,
              color: onAccent,
              fontSize: 46,
              fontWeight: 800,
              padding: '14px 36px',
              borderRadius: 999,
            }}
          >
            {d.badge}
          </span>
        )}
      </div>

      {/* الصورة */}
      <div
        style={{
          flex: 1,
          margin: '28px 0',
          minHeight: story ? 900 : d.size === 'PORTRAIT' ? 640 : 520,
          borderRadius: 48,
          background: d.template === 'classic' ? '#ffffff' : 'rgba(255,255,255,0.92)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          boxShadow: '0 30px 80px rgba(0,0,0,0.18)',
        }}
      >
        {d.image ? (
          <img src={d.image} alt="" style={{ width: '88%', height: '88%', objectFit: 'contain' }} />
        ) : (
          d.logo && <img src={d.logo} alt="" style={{ width: '40%', opacity: 0.85 }} />
        )}
      </div>

      {/* الاسمُ والسعر */}
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: d.headline.length > 28 ? 50 : 62, fontWeight: 800, lineHeight: 1.2 }}>
          {d.headline}
        </div>
        {d.subline && (
          <div style={{ fontSize: 36, opacity: 0.75, marginTop: 10 }}>{d.subline}</div>
        )}

        {d.showPrice && d.price !== null && (
          <div style={{ marginTop: 26, display: 'flex', gap: 28, justifyContent: 'center', alignItems: 'baseline' }}>
            <span
              style={{
                background: d.accent,
                color: onAccent,
                fontSize: 58,
                fontWeight: 900,
                padding: '12px 44px',
                borderRadius: 28,
              }}
            >
              {money(d.price)}
            </span>
            {offer && (
              <span style={{ fontSize: 46, textDecoration: 'line-through', opacity: 0.6 }}>
                {money(d.regularPrice!)}
              </span>
            )}
          </div>
        )}

        {d.footer && (
          <div style={{ marginTop: 26, fontSize: 30, opacity: 0.8 }}>{d.footer}</div>
        )}
      </div>
    </div>
  )
}
